import * as Sentry from "@sentry/browser";
import React, { useRef } from "react";
import {
  AdsConversionClient,
  AdsConversionSourceProvider,
} from "./ads_conversion";

import { GAClient, GASourceProvider } from "./ga";
import { GTMClient, GTMSourceProvider } from "./gtm";
import { HotjarClient, HotjarSourceProvider } from "./hotjar";
import { HubspotClient, HubspotSourceProvider } from "./hubspot";
import { PixelClient, PixelSourceProvider } from "./pixel";
import {
  Analytics,
  AnalyticsOptions,
  EcommerceEvent,
  GroupTraits,
  ScreenProperties,
  TrackProperties,
  UserTraits,
} from "./types";
import { MixpanelClient, MixpanelSourceProvider } from "./mixpanel";
import { LinkedInClient, LinkedInSourceProvider } from "./linkedin";
import { GA4Client, GA4SourceProvider } from "./ga4";
import { GoogleBookClient, GoogleBookSourceProvider } from "./google_book";
import { OneSignalClient, OneSignalSourceProvider } from "./onesignal";
import { CustomerioClient, CustomerioSourceProvider } from "./customerio";

interface AnalyticsContext extends Analytics {
  // This function loads up a client but does not initialize it. This is specific to ga4
  // so that instead of initializing ga4 multiple times per client (which is not allowed),
  // we can preload flexspace client first and then initialize it later with a partner's ga4 client
  preloadClient: (client: AnalyticsClient) => void;
  addClient: (client: AnalyticsClient) => void;
  initializeGA4Trackers: () => void;
  integration: { [provider: string]: Analytics };
  getSources: () => typeof sources;
}

const AnalyticsContext = React.createContext<AnalyticsContext>({
  integration: {},
  preloadClient: () => {},
  addClient: () => {},
  initializeGA4Trackers: () => {},
  group: () => {},
  register: () => {},
  identify: () => {},
  screen: () => {},
  event: () => {},
  ecommerceEvent: () => {},
  getSources: () => sources as typeof sources,
});

export const useAnalytics = () => {
  return React.useContext(AnalyticsContext);
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider = (props: AnalyticsProviderProps) => {
  const { children } = props;
  const analytics = useRef(new AnalyticsCombinator()).current;

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

const sources = {
  ga: new GASourceProvider(),
  gtm: new GTMSourceProvider(),
  hubspot: new HubspotSourceProvider(),
  hotjar: new HotjarSourceProvider(),
  pixel: new PixelSourceProvider(),
  ads_conversion: new AdsConversionSourceProvider(),
  mixpanel: new MixpanelSourceProvider(),
  linkedin: new LinkedInSourceProvider(),
  ga4: new GA4SourceProvider(),
  googleBook: new GoogleBookSourceProvider(),
  onesignal: new OneSignalSourceProvider(),
  customerio: new CustomerioSourceProvider(),
};

type AnalyticsClient =
  | GAClient
  | GTMClient
  | HubspotClient
  | HotjarClient
  | PixelClient
  | AdsConversionClient
  | MixpanelClient
  | LinkedInClient
  | GoogleBookClient
  | GA4Client
  | OneSignalClient
  | CustomerioClient;

export class AnalyticsCombinator {
  integration: { [provider: string]: Analytics } = {};
  private readonly failedProviders = new Set<string>();

  preloadClient(client: AnalyticsClient) {
    let source = this.integration[client.provider];

    switch (client.provider) {
      case "ga4":
        source = sources.ga4.preload(client);
        break;
      default:
        console.error(client, "not supported");
        return;
    }

    this.integration[client.provider] = source;
  }

  addClient(client: AnalyticsClient) {
    const existing = this.integration[client.provider];

    /**
     * we only need to initialize (except GA) client once because it sticks across all pages
     * it only lost on reloading page
     *
     * A provider that threw is never stored in `integration`, so `existing`
     * alone would let every later mount re-run an initializer that is going to
     * throw again — and `TagManager.initialize` appends another container script
     * to the document before it does, so a partner with a broken container would
     * collect one more copy of it per navigation.
     */
    if (
      client.provider !== "ga" &&
      client.provider !== "ga4" &&
      client.provider !== "onesignal" &&
      (existing || this.failedProviders.has(client.provider))
    ) {
      return;
    }

    // A partner's tag container is third-party code we neither author nor
    // review, and it runs inside our call stack: these SDKs write synchronously
    // into a `window.dataLayer` the container is already listening on, so a
    // container that throws while handling that write throws here. This runs
    // from an effect, where an escaping error costs the whole page — and no tag
    // is worth a booking. Drop the client that failed and keep serving.
    try {
      const source = this.initializeSource(client);
      if (source) {
        this.activateSource(client, source);
      }
    } catch (error) {
      this.reportInitFailure(client.provider, error);
    }
  }

  /**
   * The shared GA4 trackers initialize outside addClient, so they need the same
   * guard — it is the same vendor call reached through the other entry point on
   * this context, and most partners load GA4 this way rather than as a client.
   */
  initializeGA4Trackers() {
    try {
      sources.ga4.initialize();
    } catch (error) {
      // Unlike addClient, the client is already registered by the time this
      // runs: callers preload their trackers first, and preloadClient stores a
      // client whose page views and events go straight back into the vendor.
      // Leaving it there would hand the dispatch fan-out a client we have just
      // learned is broken.
      delete this.integration.ga4;
      this.reportInitFailure("ga4", error);
    }
  }

  private activateSource(client: AnalyticsClient, source: Analytics) {
    /**
     * Execute first page view after initialized. This runs before the client is
     * registered because it is the same third-party code as the initialize
     * above — these SDKs send that page view straight into the container — so a
     * vendor that throws on it has to be dropped too. Registering first would
     * leave a client we already know is broken in `integration`, where every
     * later page view and ecommerce event calls it again, outside this guard.
     */
    if (client.sendPageView) {
      source.screen();
    }

    this.integration[client.provider] = {
      ...source,
      sendPageView: client.sendPageView,
    };
  }

  private initializeSource(client: AnalyticsClient): Analytics | undefined {
    switch (client.provider) {
      case "ga":
        return sources.ga.initialize(client);
      case "gtm":
        return sources.gtm.initialize(client);
      case "hubspot":
        return sources.hubspot.initialize();
      case "hotjar":
        return sources.hotjar.initialize(client);
      case "pixel":
        return sources.pixel.initialize(client);
      case "ads_conversion":
        return sources.ads_conversion.initialize(client);
      case "mixpanel":
        return sources.mixpanel.initialize(client);
      case "linkedin":
        return sources.linkedin.initialize(client);
      case "ga4":
        return sources.ga4.initialize(client);
      case "google_book":
        return sources.googleBook.initialize(client);
      case "onesignal":
        return sources.onesignal.initialize(client);
      case "customerio":
        return sources.customerio.initialize(client);
      default:
        console.error(client, "not supported");
        return undefined;
    }
  }

  /**
   * One report per provider. A container that throws while initializing throws
   * on every attempt, and `ga`, `ga4` and `onesignal` deliberately re-initialize
   * on each mount, so reporting every attempt would spend the quota on a single
   * broken partner container.
   */
  private reportInitFailure(provider: string, error: unknown) {
    if (this.failedProviders.has(provider)) {
      return;
    }
    this.failedProviders.add(provider);

    // The tag is load-bearing, not just context: the error was thrown inside
    // the vendor's own script, so `processSentryEvent` would otherwise drop it
    // as third-party noise and a broken container would fail silently.
    Sentry.withScope((scope) => {
      scope.setTag("analytics_provider", provider);
      // Group by what we report, not the vendor frames it was thrown from:
      // those file this deliberate report into the same issue as the uncaught
      // crash this guard replaced, reopening it every time the guard works.
      scope.setFingerprint(["analytics-init-failure", provider]);
      Sentry.captureException(error);
    });
  }

  getSources() {
    return sources;
  }

  getIntegrations(options?: AnalyticsOptions) {
    const onlyProviders = options?.onlyProviders || [];

    // no provider specified, return all
    if (!onlyProviders.length) {
      return Object.values(this.integration);
    }

    const integrations: Analytics[] = [];

    for (const [key, value] of Object.entries(this.integration)) {
      // Don't care about provider not in the list
      if (!onlyProviders.includes(key)) {
        continue;
      }

      const client = value as Analytics;
      integrations.push(client);
    }

    return integrations;
  }

  group(groupID: string, traits?: GroupTraits) {
    this.getIntegrations().forEach((client) =>
      client.identify(groupID, traits)
    );
  }

  identify(userID: string, traits?: UserTraits, options?: AnalyticsOptions) {
    this.getIntegrations(options).forEach((client) =>
      client.identify(userID, traits)
    );
  }

  // Default will send screen event to all integrations
  // or send only for integrations specified in onlyProviders
  screen(
    name: string,
    properties?: ScreenProperties,
    options?: AnalyticsOptions
  ) {
    this.getIntegrations(options).forEach((client) =>
      client.screen(name, properties)
    );
  }

  ecommerceEvent(event: EcommerceEvent) {
    this.getIntegrations().forEach((client) => client.ecommerceEvent(event));
  }

  event(
    eventName: string,
    properties?: TrackProperties,
    options?: AnalyticsOptions
  ) {
    this.getIntegrations(options).forEach((client) =>
      client.event(eventName, properties)
    );
  }

  register(properties: TrackProperties, options?: AnalyticsOptions) {
    this.getIntegrations(options).forEach((client) =>
      client.register(properties)
    );
  }
}
